import React, { useEffect, useRef, useState } from 'react';
import { Users, IndianRupee, Wallet, TrendingUp, Activity, Clock, BarChart3, ArrowUp, ArrowDown, Target, Zap, Award, Shield, Calendar, Percent, UserCheck, DollarSign, TrendingDown, Copy, Check, Link2 } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'slices';
import { fetchAgentUsers, updateUserPoints, toggleUserStatus, clearError } from 'slices/agentUsers/agentUsers';
import { getRoleFromAccessToken } from 'helpers/jwt';
import api from 'helpers/api_helper';
import * as XLSX from 'xlsx';

interface ZeroBalancePlayer {
  id: number;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  mobile_number: string;
  points: string;
  points_formatted?: string;
  is_active: boolean;
  last_login?: string | null;
  last_login_formatted?: string | null;
}

type ZeroBalanceListMode = 'all' | 'active';

interface ManualDeductionRow {
  journal_id: number;
  transaction_id: number;
  player_id: number;
  player_name?: string | null;
  player_mobile: string;
  amount: string;
  balance_before: string;
  balance_after: string;
  deducted_by: {
    id?: number | null;
    name?: string | null;
    mobile_number?: string | null;
    role?: string | null;
  };
  remark?: string | null;
  description?: string | null;
  created_at?: string | null;
}

interface WithdrawalReportRow {
  withdrawal_id: number;
  player_id: number;
  player_name?: string | null;
  player_mobile: string;
  amount: string;
  status: string;
  gateway?: string | null;
  gateway_transaction_id?: string | null;
  bank_transaction_id?: string | null;
  created_at?: string | null;
  reviewed_at?: string | null;
  processed_at?: string | null;
  effective_date?: string | null;
}

interface ManualDepositRow {
  source: string;
  source_label?: string | null;
  reference_id: number;
  journal_id?: number | null;
  transaction_id?: number | null;
  player_id: number;
  player_name?: string | null;
  player_mobile: string;
  amount: string;
  balance_before?: string | null;
  balance_after?: string | null;
  added_by: {
    id?: number | null;
    name?: string | null;
    mobile_number?: string | null;
    role?: string | null;
  };
  remark?: string | null;
  utr_number?: string | null;
  mode?: string | null;
  description?: string | null;
  created_at?: string | null;
}

interface DashboardData {
  summary: {
    total_markets: number;
    total_users: number;
    users_today: number;
    active_markets: number;
    pending_bets: number;
    completed_bets: number;
    zero_balance_users: number;
    active_zero_balance_users: number;
    total_registered_zero_balance?: number; // Optional - not always in API response
    logged_in_users_today: number;
    blocked_users: number;
    sms_used_today: number;
  };
  referral_info?: {
    referral_code?: string;
    referral_link?: string;
    referral_slug?: string;
    uses_count?: number;
    max_uses?: number;
    is_usable?: boolean;
    created_at?: string;
  };
  financial_today: {
    bid_amount: number;
    win_amount: number;
    deposits: number;
    withdrawals: number;
    manually_added_deposits?: number;
    manual_withdrawals?: number;
  };
  financial_total: {
    total_deposits: number;
    total_withdrawals: number;
    wallet_balance: number;
    manually_added_balance: number;
  };
  recent_activity: {
    recent_market_results: Array<{
      market_name: string;
      open_result: string | null;
      close_result: string | null;
      jodi: string | null;
      last_updated: string;
    }>;
    last_updated: string;
  };
  user_info: {
    user_id: number;
    mobile_number: string;
    app_name: string;
    is_staff: boolean;
  };
  agent_commission?: {
    commission_percentage: number;
    referred_users_count: number;
    total_stake: number;
    lost_stake: number;
    payout_total: number;
    house_profit: number;
    commission_due: number;
    period: string;
  };
}

const Dashboard = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { users: agentUsers, loading: agentUsersLoading, error: agentUsersError } = useSelector((state: RootState) => state.AgentUsers);
  
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const role = (getRoleFromAccessToken() || '').toLowerCase();
  const isFinance = role === 'finance';
  const isAgentInternal = role === 'agent (internal)';
  const [copiedReferral, setCopiedReferral] = useState(false);
  // Date range for API params
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const dateFromInputRef = useRef<HTMLInputElement>(null);
  const dateToInputRef = useRef<HTMLInputElement>(null);
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);
  const [activeRange, setActiveRange] = useState<'today' | '7days' | 'month' | 'all' | 'custom'>('today');

  const [registeredUsersModalOpen, setRegisteredUsersModalOpen] = useState(false);
  const [registeredUsers, setRegisteredUsers] = useState<any[]>([]);
  const [registeredUsersLoading, setRegisteredUsersLoading] = useState(false);
  const [registeredUsersError, setRegisteredUsersError] = useState<string | null>(null);
  const [registeredUsersPage, setRegisteredUsersPage] = useState(1);
  const [registeredUsersTotal, setRegisteredUsersTotal] = useState(0);
  const [registeredUsersExporting, setRegisteredUsersExporting] = useState(false);
  const registeredUsersPageSize = 20;

  const [zeroBalanceModalOpen, setZeroBalanceModalOpen] = useState(false);
  const [zeroBalanceMode, setZeroBalanceMode] =
    useState<ZeroBalanceListMode>('all');
  const [zeroBalancePlayers, setZeroBalancePlayers] =
    useState<ZeroBalancePlayer[]>([]);
  const [zeroBalancePlayersLoading, setZeroBalancePlayersLoading] =
    useState(false);
  const [zeroBalancePlayersError, setZeroBalancePlayersError] =
    useState<string | null>(null);
  const [zeroBalancePage, setZeroBalancePage] = useState(1);
  const [zeroBalanceTotal, setZeroBalanceTotal] = useState(0);
  const [zeroBalanceExporting, setZeroBalanceExporting] = useState(false);

  const zeroBalancePageSize = 20;

  const [manualDeductionModalOpen, setManualDeductionModalOpen] =
    useState(false);
  const [manualDeductionRows, setManualDeductionRows] =
    useState<ManualDeductionRow[]>([]);
  const [manualDeductionLoading, setManualDeductionLoading] =
    useState(false);
  const [manualDeductionError, setManualDeductionError] =
    useState<string | null>(null);
  const [manualDeductionDateFrom, setManualDeductionDateFrom] =
    useState('');
  const [manualDeductionDateTo, setManualDeductionDateTo] =
    useState('');
  const [manualDeductionPage, setManualDeductionPage] =
    useState(1);
  const [manualDeductionTotalCount, setManualDeductionTotalCount] =
    useState(0);
  const [manualDeductionTotalAmount, setManualDeductionTotalAmount] =
    useState(0);

  const manualDeductionPageSize = 20;

  const [manualDepositModalOpen, setManualDepositModalOpen] =
    useState(false);

  const [manualDepositRows, setManualDepositRows] =
    useState<ManualDepositRow[]>([]);

  const [manualDepositLoading, setManualDepositLoading] =
    useState(false);

  const [manualDepositError, setManualDepositError] =
    useState<string | null>(null);

  const [manualDepositDateFrom, setManualDepositDateFrom] =
    useState('');

  const [manualDepositDateTo, setManualDepositDateTo] =
    useState('');

  const [manualDepositPage, setManualDepositPage] =
    useState(1);

  const [manualDepositTotalCount, setManualDepositTotalCount] =
    useState(0);

  const [manualDepositTotalAmount, setManualDepositTotalAmount] =
    useState(0);

  const manualDepositPageSize = 20;

  const [withdrawalReportModalOpen, setWithdrawalReportModalOpen] =
    useState(false);
  const [withdrawalReportRows, setWithdrawalReportRows] =
    useState<WithdrawalReportRow[]>([]);
  const [withdrawalReportLoading, setWithdrawalReportLoading] =
    useState(false);
  const [withdrawalReportError, setWithdrawalReportError] =
    useState<string | null>(null);
  const [withdrawalReportDateFrom, setWithdrawalReportDateFrom] =
    useState('');
  const [withdrawalReportDateTo, setWithdrawalReportDateTo] =
    useState('');
  const [withdrawalReportPage, setWithdrawalReportPage] =
    useState(1);
  const [withdrawalReportTotalCount, setWithdrawalReportTotalCount] =
    useState(0);
  const [withdrawalReportTotalAmount, setWithdrawalReportTotalAmount] =
    useState(0);
  const [withdrawalReportAllTimeTotal, setWithdrawalReportAllTimeTotal] =
    useState(0);

  const withdrawalReportPageSize = 20;

  useEffect(() => {
    fetchDashboardData();
    // Fetch agent users if user is agent internal
    if (isAgentInternal) {
      dispatch(fetchAgentUsers({ page: 1, page_size: 25 }));
    }
  }, [dispatch, isAgentInternal]);

  // Initialize Today using the India business date.
  // Do not derive it from the browser timezone or API timestamp.
  useEffect(() => {
    if (
      activeRange === 'today' &&
      !dateFrom &&
      !dateTo
    ) {
      const today = getTodayDate();
      setDateFrom(today);
      setDateTo(today);
    }
  }, [
    dateFrom,
    dateTo,
    activeRange
  ]);

  // Automatically roll an open Today dashboard onto the new
  // Asia/Kolkata business date after India midnight.
  useEffect(() => {
    if (activeRange !== 'today') {
      return;
    }

    const checkBusinessDateRollover = () => {
      const today = getTodayDate();

      if (dateFrom !== today || dateTo !== today) {
        setDateFrom(today);
        setDateTo(today);
        setDateRangeError(null);
        fetchDashboardData(true, today, today);
      }
    };

    // Check immediately and then regularly in case the page remains
    // open across the India business-day boundary.
    checkBusinessDateRollover();

    const intervalId = window.setInterval(
      checkBusinessDateRollover,
      30 * 1000
    );

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    activeRange,
    dateFrom,
    dateTo
  ]);

  // Check API availability
  const checkApiAvailability = async () => {
    try {
      const response = await fetch('$process.env.PUBLIC_URL/api/health', { 
        method: 'HEAD',
        mode: 'no-cors' // This will help detect if the domain is reachable
      });
      return true;
    } catch (error) {
      console.log('API not available:', error);
      return false;
    }
  };

  const fetchDashboardData = async (isRetry: boolean = false, overrideFrom?: string, overrideTo?: string) => {
    try {
      setLoading(true);
      if (!isRetry) {
        setError(null);
        setRetryCount(0);
      }
      
      // Check if we have a valid token
      const token = localStorage.getItem('accessToken');
      console.log("TOKEN:", token);
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }
      // Build params from app_id and date range
      const appId = localStorage.getItem('app_id') || localStorage.getItem('selectedAppId') || undefined;
      const df = overrideFrom || dateFrom || undefined;
      const dt = overrideTo || dateTo || df || undefined;
      const params: any = {};
      if (appId) params.app_id = appId;
      if (df) params.date_from = df;
      if (dt) params.date_to = dt;

      console.log("Params in Dashboard index:", params);
      const res = await api.get('/dashboard/', { params });
      const payload = res.data?.data;
      console.log("Payload in DI:", payload);
      
      if (!payload) {
        throw new Error('Invalid response format from server');
      }
      
      setDashboardData(payload);
      setRetryCount(0);
    } catch (err: any) {
      console.error('Dashboard fetch error:', err);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to fetch dashboard data';
      
      if (err.code === 'NETWORK_ERROR' || err.message === 'Network Error') {
        errorMessage = 'Network connection failed. Please check your internet connection and try again.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Session expired. Please login again.';
      } else if (err.response?.status === 403) {
        errorMessage = 'Access denied. You do not have permission to view this data.';
      } else if (err.response?.status === 404) {
        errorMessage = 'Dashboard endpoint not found. Please contact support.';
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later or contact support.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      
      // Never substitute fake dashboard values in production.
      setDashboardData(null);

      console.error('Dashboard API error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message
      });
    } finally {
      setLoading(false);
    }
  };

  const retryWithBackoff = async () => {
    const maxRetries = 3;
    if (retryCount < maxRetries) {
      setRetryCount(prev => prev + 1);
      const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s
      setTimeout(() => {
        fetchDashboardData(true);
      }, delay);
    } else {
      setDashboardData(null);
      setError(
        'Dashboard data is unavailable after multiple retry attempts.'
      );
    }
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const formatCurrency = (num: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(num);
  };

  const formatDateOnly = (dateString: string) => {
    const d = new Date(dateString);
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatIndiaDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Business date must match Django's Asia/Kolkata timezone.
  const getTodayDate = (): string => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());

    const year = parts.find((part) => part.type === 'year')?.value || '';
    const month = parts.find((part) => part.type === 'month')?.value || '';
    const day = parts.find((part) => part.type === 'day')?.value || '';

    return `${year}-${month}-${day}`;
  };

  const [withdrawalReportAppliedFrom, setWithdrawalReportAppliedFrom] =
    useState('');
  const [withdrawalReportAppliedTo, setWithdrawalReportAppliedTo] =
    useState('');

  const loadWithdrawalReport = async (
    page = 1,
    overrideFrom?: string,
    overrideTo?: string,
  ) => {
    try {
      setWithdrawalReportLoading(true);
      setWithdrawalReportError(null);

      const effectiveFrom =
        overrideFrom !== undefined
          ? overrideFrom
          : withdrawalReportDateFrom;

      const effectiveTo =
        overrideTo !== undefined
          ? overrideTo
          : withdrawalReportDateTo;

      const appId =
        localStorage.getItem('app_id') ||
        localStorage.getItem('selectedAppId') ||
        undefined;

      const params: Record<string, string | number> = {
        action: 'withdrawal-report',
        page,
        page_size: withdrawalReportPageSize,
      };

      if (appId) {
        params.app_id = appId;
      }

      if (effectiveFrom) {
        params.date_from = effectiveFrom;
      }

      if (effectiveTo) {
        params.date_to = effectiveTo;
      }

      const response = await api.get('/dashboard/', { params });
      const payload = response.data?.data;

      if (!payload || !Array.isArray(payload.results)) {
        throw new Error('Invalid withdrawal report response.');
      }

      setWithdrawalReportRows(payload.results);
      setWithdrawalReportTotalCount(
        Number(payload.summary?.transaction_count || 0),
      );
      setWithdrawalReportTotalAmount(
        Number(payload.summary?.total_amount || 0),
      );
      setWithdrawalReportAllTimeTotal(
        Number(payload.summary?.all_time_total || 0),
      );
      setWithdrawalReportPage(page);
      setWithdrawalReportAppliedFrom(effectiveFrom);
      setWithdrawalReportAppliedTo(effectiveTo);
    } catch (err: any) {
      console.error('Withdrawal report error:', err);

      setWithdrawalReportRows([]);
      setWithdrawalReportTotalCount(0);
      setWithdrawalReportTotalAmount(0);
      setWithdrawalReportAllTimeTotal(0);

      setWithdrawalReportError(
        err.response?.data?.error ||
          err.response?.data?.detail ||
          err.message ||
          'Failed to load withdrawal report.',
      );
    } finally {
      setWithdrawalReportLoading(false);
    }
  };

  const openWithdrawalReport = () => {
    const effectiveFrom = dateFrom;
    const effectiveTo = dateTo;

    if (!effectiveFrom || !effectiveTo || effectiveFrom > effectiveTo) {
      setWithdrawalReportError(
        'Please select a valid dashboard date range before opening the withdrawal report.',
      );
      setWithdrawalReportModalOpen(true);
      return;
    }

    setWithdrawalReportDateFrom(effectiveFrom);
    setWithdrawalReportDateTo(effectiveTo);
    setWithdrawalReportPage(1);
    setWithdrawalReportModalOpen(true);

    loadWithdrawalReport(1, effectiveFrom, effectiveTo);
  };

  const closeWithdrawalReport = () => {
    setWithdrawalReportModalOpen(false);
    setWithdrawalReportError(null);
  };

  const loadManualDeposits = async (
    page = 1,
    overrideFrom?: string,
    overrideTo?: string,
  ) => {
    try {
      setManualDepositLoading(true);
      setManualDepositError(null);

      const effectiveFrom =
        overrideFrom !== undefined
          ? overrideFrom
          : manualDepositDateFrom;

      const effectiveTo =
        overrideTo !== undefined
          ? overrideTo
          : manualDepositDateTo;

      const appId =
        localStorage.getItem('app_id') ||
        localStorage.getItem('selectedAppId') ||
        undefined;

      const params: Record<string, string | number> = {
        page,
        page_size: manualDepositPageSize,
      };

      if (appId) {
        params.app_id = appId;
      }

      if (effectiveFrom) {
        params.date_from = effectiveFrom;
      }

      if (effectiveTo) {
        params.date_to = effectiveTo;
      }

      const response = await api.get(
        '/dashboard/manual-deposits/',
        { params },
      );

      const payload = response.data?.data || {};

      setManualDepositRows(payload.results || []);

      setManualDepositTotalCount(
        Number(payload.summary?.transaction_count || 0),
      );

      setManualDepositTotalAmount(
        Number(payload.summary?.total_amount || 0),
      );

      setManualDepositPage(page);
    } catch (err: any) {
      console.error('Manual deposit report error:', err);

      setManualDepositRows([]);
      setManualDepositTotalCount(0);
      setManualDepositTotalAmount(0);

      setManualDepositError(
        err.response?.data?.error ||
          err.response?.data?.detail ||
          err.message ||
          'Failed to load manual deposits.',
      );
    } finally {
      setManualDepositLoading(false);
    }
  };

  const openManualDeposits = () => {
    const today = getTodayDate();

    setManualDepositDateFrom(today);
    setManualDepositDateTo(today);
    setManualDepositPage(1);
    setManualDepositModalOpen(true);

    loadManualDeposits(
      1,
      today,
      today,
    );
  };

  const closeManualDeposits = () => {
    setManualDepositModalOpen(false);
    setManualDepositError(null);
  };

  const applyManualDepositFilters = () => {
    if (
      manualDepositDateFrom &&
      manualDepositDateTo &&
      manualDepositDateFrom > manualDepositDateTo
    ) {
      setManualDepositError(
        'Date From cannot be after Date To.',
      );
      return;
    }

    loadManualDeposits(
      1,
      manualDepositDateFrom,
      manualDepositDateTo,
    );
  };

  const resetManualDepositFilters = () => {
    const today = getTodayDate();

    setManualDepositDateFrom(today);
    setManualDepositDateTo(today);

    loadManualDeposits(
      1,
      today,
      today,
    );
  };

  const loadManualDeductions = async (
    page = 1,
    overrideFrom?: string,
    overrideTo?: string,
  ) => {
    try {
      setManualDeductionLoading(true);
      setManualDeductionError(null);

      const effectiveFrom =
        overrideFrom !== undefined
          ? overrideFrom
          : manualDeductionDateFrom;

      const effectiveTo =
        overrideTo !== undefined
          ? overrideTo
          : manualDeductionDateTo;

      const appId =
        localStorage.getItem('app_id') ||
        localStorage.getItem('selectedAppId') ||
        undefined;

      const params: Record<string, string | number> = {
        page,
        page_size: manualDeductionPageSize,
      };

      if (appId) {
        params.app_id = appId;
      }

      if (effectiveFrom) {
        params.date_from = effectiveFrom;
      }

      if (effectiveTo) {
        params.date_to = effectiveTo;
      }

      const response = await api.get(
        '/dashboard/manual-deductions/',
        { params },
      );

      const payload = response.data?.data || {};

      setManualDeductionRows(payload.results || []);
      setManualDeductionTotalCount(
        Number(payload.summary?.transaction_count || 0),
      );
      setManualDeductionTotalAmount(
        Number(payload.summary?.total_amount || 0),
      );
      setManualDeductionPage(page);
    } catch (err: any) {
      console.error('Manual deduction report error:', err);

      setManualDeductionRows([]);
      setManualDeductionTotalCount(0);
      setManualDeductionTotalAmount(0);

      setManualDeductionError(
        err.response?.data?.error ||
          err.response?.data?.detail ||
          err.message ||
          'Failed to load manual deductions.',
      );
    } finally {
      setManualDeductionLoading(false);
    }
  };

  const openManualDeductions = () => {
    const today = getTodayDate();

    setManualDeductionDateFrom(today);
    setManualDeductionDateTo(today);
    setManualDeductionPage(1);
    setManualDeductionModalOpen(true);

    loadManualDeductions(
      1,
      today,
      today,
    );
  };

  const closeManualDeductions = () => {
    setManualDeductionModalOpen(false);
    setManualDeductionError(null);
  };

  const applyManualDeductionFilters = () => {
    if (
      manualDeductionDateFrom &&
      manualDeductionDateTo &&
      manualDeductionDateFrom > manualDeductionDateTo
    ) {
      setManualDeductionError(
        'Date From cannot be after Date To.',
      );
      return;
    }

    loadManualDeductions(
      1,
      manualDeductionDateFrom,
      manualDeductionDateTo,
    );
  };

  const resetManualDeductionFilters = () => {
    const today = getTodayDate();

    setManualDeductionDateFrom(today);
    setManualDeductionDateTo(today);

    loadManualDeductions(
      1,
      today,
      today,
    );
  };

  const loadRegisteredUsers = async (page = 1) => {
    try {
      setRegisteredUsersLoading(true);
      setRegisteredUsersError(null);

      const effectiveFrom = dateFrom || getTodayDate();
      const effectiveTo = dateTo || effectiveFrom;

      const params: Record<string, string | number> = {
        action: 'registered-users',
        date_from: effectiveFrom,
        date_to: effectiveTo,
        page,
        page_size: registeredUsersPageSize,
      };

      const response = await api.get('/dashboard/', { params });

      setRegisteredUsers(response.data?.registered_users || []);
      setRegisteredUsersTotal(
        Number(response.data?.filtered_count || 0),
      );
      setRegisteredUsersPage(page);
    } catch (err: any) {
      console.error('Registered users list error:', err);
      setRegisteredUsers([]);
      setRegisteredUsersTotal(0);
      setRegisteredUsersError(
        err.response?.data?.error ||
          err.response?.data?.detail ||
          err.message ||
          'Failed to load registered users.',
      );
    } finally {
      setRegisteredUsersLoading(false);
    }
  };

  const openRegisteredUsers = () => {
    setRegisteredUsersModalOpen(true);
    loadRegisteredUsers(1);
  };

  const closeRegisteredUsers = () => {
    setRegisteredUsersModalOpen(false);
    setRegisteredUsersError(null);
  };

  const exportRegisteredUsersToExcel = async () => {
    try {
      setRegisteredUsersExporting(true);
      setRegisteredUsersError(null);

      const effectiveFrom = dateFrom || getTodayDate();
      const effectiveTo = dateTo || effectiveFrom;

      const pageSize = 1000;
      let page = 1;
      let total = 0;
      const allUsers: any[] = [];

      do {
        const params: Record<string, string | number> = {
          action: 'registered-users',
          date_from: effectiveFrom,
          date_to: effectiveTo,
          page,
          page_size: pageSize,
        };

        const response = await api.get('/dashboard/', { params });
        const users = response.data?.registered_users || [];

        total = Number(response.data?.filtered_count || 0);
        allUsers.push(...users);

        if (users.length === 0) {
          break;
        }

        page += 1;
      } while (allUsers.length < total);

      if (allUsers.length === 0) {
        setRegisteredUsersError(
          'No registered users found to export.',
        );
        return;
      }

      const excelRows = allUsers.map((user) => ({
        'User ID': user.id,
        'Name':
          user.full_name ||
          `${user.first_name || ''} ${user.last_name || ''}`.trim(),
        'Mobile Number': user.mobile_number || '',
        'Status': user.is_active ? 'Active' : 'Inactive',
        'Joined': user.date_joined
          ? formatIndiaDateTime(user.date_joined)
          : '',
        'App': user.app_name || '',
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelRows);

      worksheet['!cols'] = [
        { wch: 12 },
        { wch: 28 },
        { wch: 18 },
        { wch: 12 },
        { wch: 24 },
        { wch: 20 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        'Registered Users',
      );

      const suffix =
        effectiveFrom === effectiveTo
          ? effectiveFrom
          : `${effectiveFrom}_to_${effectiveTo}`;

      XLSX.writeFile(
        workbook,
        `registered-users-${suffix}.xlsx`,
      );
    } catch (err: any) {
      console.error('Registered users Excel export error:', err);
      setRegisteredUsersError(
        err.response?.data?.error ||
          err.response?.data?.detail ||
          err.message ||
          'Failed to export registered users.',
      );
    } finally {
      setRegisteredUsersExporting(false);
    }
  };

  const loadZeroBalancePlayers = async (
    mode: ZeroBalanceListMode,
    page = 1,
  ) => {
    try {
      setZeroBalancePlayersLoading(true);
      setZeroBalancePlayersError(null);

      const appId =
        localStorage.getItem('app_id') ||
        localStorage.getItem('selectedAppId') ||
        undefined;

      const params: Record<string, string | number> = {
        balance_filter: 'zero',
        is_staff: 'false',
        page,
        page_size: zeroBalancePageSize,
        reverse: 'true',
      };

      if (appId) {
        params.app_id = appId;
      }

      if (mode === 'active') {
        const effectiveFrom = dateFrom || getTodayDate();
        const effectiveTo = dateTo || effectiveFrom;

        params.is_active = 'true';
        params.activity_filter = 'last_login';
        params.date_from = effectiveFrom;
        params.date_to = effectiveTo;
        params.order_by = 'last_login';
      } else {
        params.order_by = 'created_at';
      }

      const response = await api.get('/users/', { params });

      setZeroBalancePlayers(response.data?.users || []);
      setZeroBalanceTotal(
        Number(response.data?.filtered_count || 0),
      );
      setZeroBalancePage(page);
    } catch (err: any) {
      console.error('Zero balance player list error:', err);
      setZeroBalancePlayers([]);
      setZeroBalanceTotal(0);
      setZeroBalancePlayersError(
        err.response?.data?.error ||
          err.response?.data?.detail ||
          err.message ||
          'Failed to load zero balance players.',
      );
    } finally {
      setZeroBalancePlayersLoading(false);
    }
  };

  const openZeroBalancePlayers = (
    mode: ZeroBalanceListMode,
  ) => {
    setZeroBalanceMode(mode);
    setZeroBalanceModalOpen(true);
    loadZeroBalancePlayers(mode, 1);
  };

  const closeZeroBalancePlayers = () => {
    setZeroBalanceModalOpen(false);
    setZeroBalancePlayersError(null);
  };

  const exportZeroBalancePlayersToExcel = async () => {
    try {
      setZeroBalanceExporting(true);
      setZeroBalancePlayersError(null);

      const appId =
        localStorage.getItem('app_id') ||
        localStorage.getItem('selectedAppId') ||
        undefined;

      const pageSize = 1000;
      let page = 1;
      let total = 0;
      const allPlayers: ZeroBalancePlayer[] = [];

      do {
        const params: Record<string, string | number> = {
          balance_filter: 'zero',
          is_staff: 'false',
          page,
          page_size: pageSize,
          reverse: 'true',
        };

        if (appId) {
          params.app_id = appId;
        }

        if (zeroBalanceMode === 'active') {
          const effectiveFrom = dateFrom || getTodayDate();
          const effectiveTo = dateTo || effectiveFrom;

          params.is_active = 'true';
          params.activity_filter = 'last_login';
          params.date_from = effectiveFrom;
          params.date_to = effectiveTo;
          params.order_by = 'last_login';
        } else {
          params.order_by = 'created_at';
        }

        const response = await api.get('/users/', { params });
        const users: ZeroBalancePlayer[] = response.data?.users || [];

        total = Number(response.data?.filtered_count || 0);
        allPlayers.push(...users);

        if (users.length === 0) {
          break;
        }

        page += 1;
      } while (allPlayers.length < total);

      if (allPlayers.length === 0) {
        setZeroBalancePlayersError(
          'No zero balance players found to export.',
        );
        return;
      }

      const excelRows = allPlayers.map((player) => {
        const playerName =
          player.full_name?.trim() ||
          `${player.first_name || ''} ${player.last_name || ''}`.trim() ||
          `Player #${player.id}`;

        return {
          'Player ID': player.id,
          'Player Name': playerName,
          'Mobile Number': player.mobile_number || '',
          'Wallet Balance': Number(player.points || 0),
          'Status': player.is_active ? 'Active' : 'Inactive',
          'Last Login': player.last_login
            ? formatDate(player.last_login)
            : 'Never',
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(excelRows);

      worksheet['!cols'] = [
        { wch: 12 },
        { wch: 28 },
        { wch: 18 },
        { wch: 16 },
        { wch: 12 },
        { wch: 24 },
      ];

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        zeroBalanceMode === 'active'
          ? 'Active Zero Balance'
          : 'Zero Balance Players',
      );

      const today = getTodayDate();
      const filename =
        zeroBalanceMode === 'active'
          ? `active-zero-balance-players-${today}.xlsx`
          : `zero-balance-players-${today}.xlsx`;

      XLSX.writeFile(workbook, filename);
    } catch (err: any) {
      console.error('Zero balance Excel export error:', err);

      setZeroBalancePlayersError(
        err.response?.data?.error ||
          err.response?.data?.detail ||
          err.message ||
          'Failed to export zero balance players.',
      );
    } finally {
      setZeroBalanceExporting(false);
    }
  };

  const formatDateForApi = (date: Date): string => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const handleRangePreset = (
    range: 'today' | '7days' | 'month' | 'all' | 'custom'
  ) => {
    setActiveRange(range);
    setDateRangeError(null);

    if (range === 'custom') {
      return;
    }

    const todayStr = getTodayDate();
    const [year, month, day] = todayStr.split('-').map(Number);
    const today = new Date(year, month - 1, day, 12, 0, 0);

    if (range === 'all') {
      const allTimeStart = '2000-01-01';

      setDateFrom(allTimeStart);
      setDateTo(todayStr);
      fetchDashboardData(true, allTimeStart, todayStr);
      return;
    }

    let start = new Date(today);

    if (range === '7days') {
      start.setDate(today.getDate() - 6);
    } else if (range === 'month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
    }

    const startStr = formatDateForApi(start);
    setDateFrom(startStr);
    setDateTo(todayStr);
    fetchDashboardData(true, startStr, todayStr);
  };

  const selectedPeriodLabel =
    activeRange === 'today'
      ? 'Today'
      : activeRange === '7days'
        ? '7 Days'
        : activeRange === 'month'
          ? 'This Month'
          : activeRange === 'all'
            ? 'All Time'
            : 'Custom';

  // Agent Users handlers
  const handleUpdatePoints = async (userId: number, points: number) => {
    try {
      await dispatch(updateUserPoints({ userId, points })).unwrap();
      // Refresh the agent users list
      dispatch(fetchAgentUsers({ page: 1, page_size: 25 }));
    } catch (error) {
      console.error('Failed to update points:', error);
    }
  };

  const handleToggleUserStatus = async (userId: number, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'active' ? 'blocked' : 'active';
      await dispatch(toggleUserStatus({ userId, status: newStatus as 'active' | 'blocked' })).unwrap();
      // Refresh the agent users list
      dispatch(fetchAgentUsers({ page: 1, page_size: 25 }));
    } catch (error) {
      console.error('Failed to toggle user status:', error);
    }
  };

  const handleEditUser = (userId: number) => {
    // TODO: Implement edit user functionality
    console.log('Edit user:', userId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-8 shadow-sm border max-w-sm w-full text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-8 shadow-sm border max-w-md w-full text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Activity className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Something went wrong</h3>
          <p className="text-red-600 mb-2">{error}</p>
          {retryCount > 0 && (
            <p className="text-sm text-gray-500 mb-4">
              Retry attempt {retryCount} of 3
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-3">
            <button 
              onClick={() => fetchDashboardData()}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-8 shadow-sm border max-w-md w-full text-center">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-6 h-6 text-gray-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No data available</h3>
          <p className="text-gray-600 mb-4">Dashboard data is currently unavailable</p>
          <button 
            onClick={() => fetchDashboardData()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
        @keyframes pulse-slow {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }
        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }
        /* Hide default browser date picker icon */
        input[type="date"]::-webkit-calendar-picker-indicator {
          display: none;
          -webkit-appearance: none;
        }
        input[type="date"]::-webkit-inner-spin-button,
        input[type="date"]::-webkit-clear-button {
          display: none;
          -webkit-appearance: none;
        }
        input[type="date"] {
          -webkit-appearance: none;
        }
      `}</style>
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">

        {/* Header */}
        <div className="mb-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border relative pb-10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
                  Dashboard Analytics
                </h1>
                <p className="text-gray-600">Real-time insights and performance metrics</p>
                  {isAgentInternal && dashboardData?.referral_info?.referral_link && (
                    <div className="mt-4 animate-fade-in">
                      <div className="relative group">
                        {/* Animated gradient background */}
                        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-pink-500/20 blur-xl group-hover:from-indigo-500/30 group-hover:via-purple-500/30 group-hover:to-pink-500/30 transition-all duration-500"></div>
                        <div className="relative rounded-xl border border-indigo-200/50 bg-gradient-to-br from-indigo-50/80 via-white to-purple-50/80 backdrop-blur-sm shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                          {/* Animated shimmer effect */}
                          <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
                          
                          <div className="relative p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md animate-pulse-slow">
                                  <Link2 className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                  <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">Referral Link</p>
                                  <p className="text-[10px] text-indigo-400 mt-0.5">Share to invite users</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                <span className="text-xs text-indigo-500 font-medium">Active</span>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2 bg-white/60 rounded-lg p-3 border border-indigo-100/50 backdrop-blur-sm">
                              <div className="flex-1 min-w-0">
                                <a
                                  href={dashboardData.referral_info.referral_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block text-sm font-mono text-indigo-700 hover:text-indigo-900 truncate transition-colors duration-200"
                                  title={dashboardData.referral_info.referral_link}
                                >
                                  {dashboardData.referral_info.referral_link}
                                </a>
                              </div>
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(dashboardData.referral_info!.referral_link!);
                                    setCopiedReferral(true);
                                    setTimeout(() => setCopiedReferral(false), 2000);
                                  } catch (err) {
                                    console.error('Failed to copy:', err);
                                  }
                                }}
                                className={`flex-shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-xs transition-all duration-300 transform hover:scale-105 active:scale-95 ${
                                  copiedReferral
                                    ? 'bg-green-500 text-white shadow-lg shadow-green-500/50'
                                    : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700 shadow-md hover:shadow-lg'
                                }`}
                                title="Copy referral link"
                              >
                                {copiedReferral ? (
                                  <>
                                    <Check className="w-4 h-4 animate-pulse" />
                                    <span className="animate-pulse">Copied!</span>
                                  </>
                                ) : (
                                  <>
                                    <Copy className="w-4 h-4" />
                                    <span>Copy</span>
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
              </div>
              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-end gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  {[
                    { key: 'today', label: 'Today' },
                    { key: '7days', label: '7 Days' },
                    { key: 'month', label: 'This Month' },
                    { key: 'all', label: 'All Time' },
                    { key: 'custom', label: 'Custom' },
                  ].map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => handleRangePreset(
                        item.key as 'today' | '7days' | 'month' | 'all' | 'custom'
                      )}
                      className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                        activeRange === item.key
                          ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1.5 rounded-lg text-sm font-medium">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  Live Data
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600 font-medium">From:</label>
                    <div className="relative inline-flex items-center">
                      <input
                        ref={dateFromInputRef}
                        type="date"
                        value={dateFrom}
                        onChange={(e) => {
                          const v = e.target.value;
                          const today = getTodayDate();
                          
                          // Validate: From Date cannot be in the future
                          if (v > today) {
                            setDateRangeError('From date cannot be in the future');
                            return;
                          }
                          
                          setDateFrom(v);
                          setActiveRange('custom');
                          setDateRangeError(null);
                          
                          // If To Date is before From Date, clear To Date
                          if (dateTo && v > dateTo) {
                            setDateTo('');
                          }
                          
                          // Call API only when both dates are selected
                          if (v && dateTo && v <= dateTo) {
                            fetchDashboardData(true, v, dateTo);
                          }
                        }}
                        max={getTodayDate()}
                        className="pl-2 pr-8 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer w-auto"
                        aria-label="From date"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          dateFromInputRef.current?.showPicker?.();
                          dateFromInputRef.current?.click();
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer hover:text-blue-600 transition-colors"
                        title="Open date picker"
                      >
                        <Calendar className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600 font-medium">To:</label>
                    <div className="relative inline-flex items-center">
                      <input
                        ref={dateToInputRef}
                        type="date"
                        value={dateTo}
                        onChange={(e) => {
                          const v = e.target.value;
                          const today = getTodayDate();
                          
                          // Validate: To Date cannot be in the future
                          if (v > today) {
                            setDateRangeError('To date cannot be in the future');
                            return;
                          }
                          
                          // Validate: To Date should not be before From Date
                          if (dateFrom && v < dateFrom) {
                            setDateRangeError('To date cannot be before From date');
                            return;
                          }
                          
                          setDateTo(v);
                          setActiveRange('custom');
                          setDateRangeError(null);
                          
                          // Call API only when both dates are selected
                          if (dateFrom && v && dateFrom <= v) {
                            fetchDashboardData(true, dateFrom, v);
                          }
                        }}
                        min={dateFrom || undefined}
                        max={getTodayDate()}
                        className="pl-2 pr-8 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer w-auto"
                        aria-label="To date"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          dateToInputRef.current?.showPicker?.();
                          dateToInputRef.current?.click();
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer hover:text-blue-600 transition-colors"
                        title="Open date picker"
                      >
                        <Calendar className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                  </div>
                  {dateRangeError && (
                    <div className="text-xs text-red-600 mt-1 sm:mt-0">
                      {dateRangeError}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Agent Dashboard - Only 4 specific cards */}
        {isAgentInternal ? (
          <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Total Bids */}
            <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Total Bids</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(dashboardData.financial_today.bid_amount)}</p>
                <p className="text-xs text-gray-500 mt-1">Live Trading</p>
              </div>
            </div>

            {/* Total Wallet Amount */}
            <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-blue-600" />
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Total Wallet Amount</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(dashboardData.financial_total.wallet_balance)}</p>
                <p className="text-xs text-gray-500 mt-1">Available Funds</p>
              </div>
            </div>

            {/* Total Deposits */}
            <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <ArrowUp className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Total Deposits</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(dashboardData.financial_total.total_deposits)}</p>
                <p className="text-xs text-gray-500 mt-1">All Time</p>
              </div>
            </div>

            {/* Total Withdraw */}
            <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <ArrowDown className="w-5 h-5 text-red-600" />
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Total Withdraw</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(dashboardData.financial_total.total_withdrawals)}</p>
                <p className="text-xs text-gray-500 mt-1">All Time</p>
              </div>
            </div>
          </div>

          {/* Agent Commission Cards */}
          {dashboardData.agent_commission && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Commission Percentage */}
              <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <Percent className="w-5 h-5 text-indigo-600" />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Commission Percentage</p>
                  <p className="text-2xl font-bold text-gray-900">{dashboardData.agent_commission.commission_percentage.toFixed(2)}%</p>
                  <p className="text-xs text-gray-500 mt-1">Rate</p>
                </div>
              </div>

              {/* Referred Users Count */}
              <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <UserCheck className="w-5 h-5 text-green-600" />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Referred Users</p>
                  <p className="text-2xl font-bold text-gray-900">{formatNumber(dashboardData.agent_commission.referred_users_count)}</p>
                  <p className="text-xs text-gray-500 mt-1">Total Count</p>
                </div>
              </div>

              {/* Total Stake */}
              <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Total Stake</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(dashboardData.agent_commission.total_stake)}</p>
                  <p className="text-xs text-gray-500 mt-1">All Bets</p>
                </div>
              </div>

              {/* Lost Stake */}
              <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <TrendingDown className="w-5 h-5 text-red-600" />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Lost Stake</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(dashboardData.agent_commission.lost_stake)}</p>
                  <p className="text-xs text-gray-500 mt-1">Loss Amount</p>
                </div>
              </div>

              {/* Payout Total */}
              <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-cyan-600" />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Payout Total</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(dashboardData.agent_commission.payout_total)}</p>
                  <p className="text-xs text-gray-500 mt-1">Total Payout</p>
                </div>
              </div>

              {/* House Profit */}
              <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">House Profit</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(dashboardData.agent_commission.house_profit)}</p>
                  <p className="text-xs text-gray-500 mt-1">Net Profit</p>
                </div>
              </div>

              {/* Commission Due */}
              <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Award className="w-5 h-5 text-amber-600" />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Commission Due</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(dashboardData.agent_commission.commission_due)}</p>
                  <p className="text-xs text-gray-500 mt-1">Payable Amount</p>
                </div>
              </div>

              
            </div>
          )}

          {/* Agent Users Table */}
          <div className="mt-8">
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Agent Users</h3>
                <p className="text-sm text-gray-600">Manage and view all agent users</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Points</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Login Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {agentUsersLoading ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-8 text-center">
                          <div className="flex items-center justify-center">
                            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                            <span className="text-gray-600">Loading agent users...</span>
                        </div>
                      </td>
                      </tr>
                    ) : agentUsersError ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-8 text-center">
                          <div className="text-red-600">
                            <p className="font-medium">Error loading agent users</p>
                            <p className="text-sm text-gray-500 mt-1">{agentUsersError}</p>
                            <button 
                              onClick={() => dispatch(fetchAgentUsers({ page: 1, page_size: 25 }))}
                              className="mt-2 text-blue-600 hover:text-blue-800 text-sm underline"
                            >
                              Try again
                          </button>
                        </div>
                      </td>
                    </tr>
                    ) : agentUsers.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-8 text-center">
                          <div className="text-gray-500">
                            <p className="font-medium">No agent users found</p>
                            <p className="text-sm mt-1">No users have been assigned to this agent yet.</p>
                        </div>
                      </td>
                    </tr>
                    ) : (
                      agentUsers.map((user: any) => {
                        // Handle API response structure - it may have user_name (single string) or first_name/last_name
                        let firstName = '';
                        let lastName = '';
                        let fullName = 'N/A';
                        
                        if ((user as any)?.user_name) {
                          // API returns user_name as single string like "keerthana f"
                          const nameParts = (user as any).user_name.trim().split(/\s+/);
                          firstName = nameParts[0] || '';
                          lastName = nameParts.slice(1).join(' ') || '';
                          fullName = (user as any).user_name || 'N/A';
                        } else {
                          // Fallback to first_name/last_name if available
                          firstName = user?.first_name || '';
                          lastName = user?.last_name || '';
                          fullName = [firstName, lastName].filter(Boolean).join(' ') || 'N/A';
                        }
                        
                        const firstInitial = firstName ? firstName.charAt(0) : '';
                        const lastInitial = lastName ? lastName.charAt(0) : '';
                        const initials = firstInitial || lastInitial 
                          ? `${firstInitial}${lastInitial}`.toUpperCase() 
                          : 'N/A';
                        
                        // Use user_id from API response (it's 'user' field in the response)
                        const userId = (user as any)?.user || user?.user_id || user?.id;
                        
                        const status = user?.status || 'unknown';
                        const statusColor = status === 'active' ? 'bg-green-100 text-green-800' : 
                                          status === 'blocked' ? 'bg-red-100 text-red-800' : 
                                          'bg-gray-100 text-gray-800';
                        const loginType = user?.login_type || 'unknown';
                        const loginTypeColor = loginType === 'password' ? 'bg-orange-100 text-blue-800' : 
                                             'bg-purple-100 text-purple-800';
                        
                        return (
                          <tr key={user?.id || Math.random()} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                                  <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                                    <span className="text-blue-600 font-semibold text-sm">{initials}</span>
                            </div>
                          </div>
                          <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {fullName}
                                  </div>
                                  <div className="text-sm text-gray-500">ID: {userId || 'N/A'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{user?.mobile_number || 'N/A'}</div>
                              {user?.email && (
                                <div className="text-sm text-gray-500">{user.email}</div>
                              )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-blue-800">
                                {user?.role || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(user?.points || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColor}`}>
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${loginTypeColor}`}>
                                {loginType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {(user?.joined_date || (user as any)?.created_at) ? formatDate(user?.joined_date || (user as any)?.created_at) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                                <button 
                                  onClick={() => handleEditUser(userId)}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-orange-50 rounded-md hover:bg-orange-100 transition-colors"
                                >
                            Edit
                          </button>
                                <button 
                                  onClick={() => {
                                    const newPoints = prompt(`Enter new points for ${fullName}:`, (user?.points || 0).toString());
                                    if (newPoints && !isNaN(Number(newPoints))) {
                                      handleUpdatePoints(userId, Number(newPoints));
                                    }
                                  }}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-yellow-600 bg-yellow-50 rounded-md hover:bg-yellow-100 transition-colors"
                                >
                            Points
                          </button>
                                <button 
                                  onClick={() => handleToggleUserStatus(userId, status)}
                                  className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                                    status === 'active' 
                                      ? 'text-red-600 bg-red-50 hover:bg-red-100' 
                                      : 'text-green-600 bg-green-50 hover:bg-green-100'
                                  }`}
                                >
                                  {status === 'active' ? 'Block' : 'Unblock'}
                          </button>
                        </div>
                      </td>
                    </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          </>
        ) : (
          <>
            {/* Main Metrics Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Total Markets (hide for Finance) */}
              {!isFinance && (
                <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Total Markets</p>
                    <p className="text-2xl font-bold text-gray-900">{formatNumber(dashboardData.summary.total_markets)}</p>
                    <p className="text-xs text-gray-500 mt-1">{dashboardData.summary.active_markets} Active</p>
                  </div>
                </div>
              )}

              {/* Total Users (hide for Finance) */}
              {!isFinance && (
                <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Total Users</p>
                    <p className="text-2xl font-bold text-gray-900">{formatNumber(dashboardData.summary.total_users)}</p>
                    <p className="text-xs text-green-600 mt-1 font-medium">+{dashboardData.summary.users_today} New - {selectedPeriodLabel}</p>
                  </div>
                </div>
              )}

              {/* Today Bids */}
              <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">{selectedPeriodLabel} Bids</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(dashboardData.financial_today.bid_amount)}</p>
                  <p className="text-xs text-gray-500 mt-1">Live Trading</p>
                </div>
              </div>

              {/* Today Wins (hide for Finance) */}
              {!isFinance && (
                <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Award className="w-5 h-5 text-orange-600" />
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">{selectedPeriodLabel} Wins</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(dashboardData.financial_today.win_amount)}</p>
                    <p className="text-xs text-gray-500 mt-1">Winners</p>
                  </div>
                </div>
              )}
            </div>

            {/* New Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <button
                type="button"
                onClick={openRegisteredUsers}
                className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow text-left cursor-pointer"
              >
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                  Users - {selectedPeriodLabel}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(dashboardData.summary.users_today)}
                </p>
                <p className="mt-1 text-xs font-medium text-blue-600">
                  View registered users
                </p>
              </button>
              <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Active Markets</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(dashboardData.summary.active_markets)}</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Pending Bets</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(dashboardData.summary.pending_bets)}</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Completed Bets</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(dashboardData.summary.completed_bets)}</p>
              </div>
              <button
                type="button"
                onClick={() => openZeroBalancePlayers('all')}
                className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow text-left cursor-pointer"
              >
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                  Zero Balance Players
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(dashboardData.summary.zero_balance_users)}
                </p>
                <p className="text-xs text-blue-600 mt-2 font-medium">
                  View players
                </p>
              </button>

              <button
                type="button"
                onClick={() => openZeroBalancePlayers('active')}
                className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow text-left cursor-pointer"
              >
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                  Active Zero Balance - {selectedPeriodLabel}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(dashboardData.summary.active_zero_balance_users)}
                </p>
                <p className="text-xs text-blue-600 mt-2 font-medium">
                  View follow-up list
                </p>
              </button>
              {/* <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Total Registered Zero Balance</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(dashboardData.summary.total_registered_zero_balance)}</p>
              </div> */}
              <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Blocked Users</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(dashboardData.summary.blocked_users)}</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">SMS Used Today</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(dashboardData.summary.sms_used_today)}</p>
              </div>
            </div>
          </>
        )}

        {/* Financial Overview (hide for Agent internal) */}
        {!isAgentInternal && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {/* Today Deposits */}
          <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                <ArrowUp className="w-4 h-4 text-emerald-600" />
              </div>
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">{selectedPeriodLabel} Deposits</p>
            </div>
            <p className="text-xl font-bold text-gray-900 mb-1">{formatCurrency(dashboardData.financial_today.deposits)}</p>
            <p className="text-xs text-gray-500">Total: {formatCurrency(dashboardData.financial_total.total_deposits)}</p>
          </div>

          {/* Today Withdrawals */}
          <button
            type="button"
            onClick={openWithdrawalReport}
            className="w-full text-left bg-white rounded-xl p-4 shadow-sm border hover:shadow-md hover:border-red-300 transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
            aria-label="Open withdrawal transaction report"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <ArrowDown className="w-4 h-4 text-red-600" />
              </div>
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">{selectedPeriodLabel} Withdrawals</p>
            </div>
            <p className="text-xl font-bold text-gray-900 mb-1">{formatCurrency(dashboardData.financial_today.withdrawals)}</p>
            <p className="text-xs text-gray-500">Total: {formatCurrency(dashboardData.financial_total.total_withdrawals)}</p>
          </button>

          {/* Manual Deposits */}
          {dashboardData.financial_today.manually_added_deposits !== undefined && (
            <button
              type="button"
              onClick={openManualDeposits}
              className="w-full text-left bg-white rounded-xl p-4 shadow-sm border hover:shadow-md hover:border-amber-300 transition-all cursor-pointer"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-4 h-4 text-amber-600" />
                </div>

                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  Manual Deposits - {selectedPeriodLabel}
                </p>
              </div>

              <p className="text-xl font-bold text-gray-900 mb-1">
                {formatCurrency(
                  dashboardData.financial_today.manually_added_deposits
                )}
              </p>

              <p className="text-xs text-amber-600 font-medium">
                Click to view detailed report
              </p>
            </button>
          )}

          {/* Manual Deductions */}
          {dashboardData.financial_today.manual_withdrawals !== undefined && (
            <button
              type="button"
              onClick={openManualDeductions}
              className="w-full text-left bg-white rounded-xl p-4 shadow-sm border hover:shadow-md hover:border-rose-300 transition-all cursor-pointer"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 bg-rose-100 rounded-lg flex items-center justify-center">
                  <ArrowDown className="w-4 h-4 text-rose-600" />
                </div>
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  Manual Deductions - {selectedPeriodLabel}
                </p>
              </div>

              <p className="text-xl font-bold text-gray-900 mb-1">
                {formatCurrency(
                  dashboardData.financial_today.manual_withdrawals
                )}
              </p>

              <p className="text-xs text-rose-600 font-medium">
                Click to view detailed report
              </p>
            </button>
          )}

          {/* Wallet Balance */}
          <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <Wallet className="w-4 h-4 text-blue-600" />
              </div>
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Wallet Balance</p>
            </div>
            <p className="text-xl font-bold text-gray-900 mb-1">{formatCurrency(dashboardData.financial_total.wallet_balance)}</p>
            <p className="text-xs text-gray-500">Available Funds</p>
          </div>

          {/* Manual Balance */}
          <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Shield className="w-4 h-4 text-indigo-600" />
              </div>
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Manual Balance</p>
            </div>
            <p className="text-xl font-bold text-gray-900 mb-1">{formatCurrency(dashboardData.financial_total.manually_added_balance)}</p>
            <p className="text-xs text-gray-500">Admin Added</p>
          </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Market Overview (hide for Finance and Agent internal) */}
          {!isFinance && !isAgentInternal && (
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <Activity className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Market Overview</h3>
                      <p className="text-sm text-gray-600">Quick summary of market activities</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Clock className="w-4 h-4" />
                    <span>Updated: {formatDate(dashboardData.recent_activity.last_updated)}</span>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div className="bg-orange-50 rounded-lg p-4 text-center">
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <TrendingUp className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-sm font-medium text-blue-600">Total Markets</p>
                    <p className="text-2xl font-bold text-blue-900">
                      {dashboardData.summary.total_markets}
                    </p>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <Activity className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-sm font-medium text-green-600">Active Markets</p>
                    <p className="text-2xl font-bold text-green-900">
                      {dashboardData.summary.active_markets}
                    </p>
                  </div>

                  {/* <div className="bg-purple-50 rounded-lg p-4 text-center">
                    <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <Clock className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-sm font-medium text-purple-600">Pending</p>
                    <p className="text-2xl font-bold text-purple-900">
                      {dashboardData.recent_activity.recent_market_results.filter(m => !m.open_result && !m.close_result).length}
                    </p>
                  </div> */}

                  <div className="bg-rose-50 rounded-lg p-4 text-center">
                    <div className="w-8 h-8 bg-rose-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <Clock className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-sm font-medium text-rose-600 text-nowrap">Closed Markets</p>
                    <p className="text-2xl font-bold text-rose-900">
                      {dashboardData.summary.total_markets - dashboardData.summary.active_markets}
                    </p>
                  </div>
                  
                </div>

                <div className="text-center">
                  <a 
                    href="/market" 
                    className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors text-sm"
                  >
                    <TrendingUp className="w-4 h-4" />
                    View Market Details
                  </a>
                  <p className="text-xs text-gray-500 mt-2">Complete market results and updates</p>
                </div>
              </div>
            </div>
          </div>
          )}

          {/* User Info & Stats */}
          
        </div>
      </div>

      {withdrawalReportModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Withdrawal transaction report"
            className="flex max-h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            <div className="border-b border-gray-200 px-5 py-4 sm:px-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Withdrawals - Detailed Report
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Read-only report of completed qualifying withdrawals.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeWithdrawalReport}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-4">
                <label className="text-xs font-semibold text-gray-600">
                  Date From
                  <input
                    type="date"
                    value={withdrawalReportDateFrom}
                    onChange={(e) => setWithdrawalReportDateFrom(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </label>
                <label className="text-xs font-semibold text-gray-600">
                  Date To
                  <input
                    type="date"
                    value={withdrawalReportDateTo}
                    onChange={(e) => setWithdrawalReportDateTo(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </label>
                <div className="flex items-end gap-2 sm:col-span-2">
                  <button
                    type="button"
                    disabled={withdrawalReportLoading}
                    onClick={() => {
                      if (
                        !withdrawalReportDateFrom ||
                        !withdrawalReportDateTo ||
                        withdrawalReportDateFrom > withdrawalReportDateTo
                      ) {
                        setWithdrawalReportError('Select a valid date range.');
                        return;
                      }
                      loadWithdrawalReport(
                        1,
                        withdrawalReportDateFrom,
                        withdrawalReportDateTo,
                      );
                    }}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    Apply
                  </button>
                  <button
                    type="button"
                    disabled={withdrawalReportLoading}
                    onClick={() => {
                      const today = getTodayDate();
                      setWithdrawalReportDateFrom(today);
                      setWithdrawalReportDateTo(today);
                      loadWithdrawalReport(1, today, today);
                    }}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Today
                  </button>
                </div>
              </div>

              {withdrawalReportError && (
                <p role="alert" className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                  {withdrawalReportError}
                </p>
              )}

              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-xl bg-red-50 p-4">
                  <p className="text-xs font-semibold uppercase text-red-700">
                    Selected Period
                  </p>
                  <p className="mt-1 text-xl font-bold text-red-900">
                    {formatCurrency(withdrawalReportTotalAmount)}
                  </p>
                </div>
                <div className="rounded-xl bg-gray-50 p-4">
                  <p className="text-xs font-semibold uppercase text-gray-600">
                    Transactions
                  </p>
                  <p className="mt-1 text-xl font-bold text-gray-900">
                    {formatNumber(withdrawalReportTotalCount)}
                  </p>
                </div>
                <div className="rounded-xl bg-gray-50 p-4">
                  <p className="text-xs font-semibold uppercase text-gray-600">
                    All-Time Withdrawals
                  </p>
                  <p className="mt-1 text-xl font-bold text-gray-900">
                    {formatCurrency(withdrawalReportAllTimeTotal)}
                  </p>
                </div>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-auto">
              {withdrawalReportLoading ? (
                <p className="p-12 text-center text-sm text-gray-500">
                  Loading withdrawals...
                </p>
              ) : withdrawalReportError ? (
                <p className="p-12 text-center text-sm text-red-700">
                  Unable to display withdrawal transactions.
                </p>
              ) : withdrawalReportRows.length === 0 ? (
                <p className="p-12 text-center text-sm text-gray-500">
                  No qualifying withdrawals found for this period.
                </p>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr>
                      {[
                        'Effective Date',
                        'Withdrawal ID',
                        'Player',
                        'Mobile',
                        'Amount',
                        'Status',
                        'Gateway',
                        'Gateway Reference',
                        'Bank Reference',
                      ].map((heading) => (
                        <th
                          key={heading}
                          className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500"
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {withdrawalReportRows.map((row) => (
                      <tr key={row.withdrawal_id} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap px-4 py-3 text-sm">
                          {row.effective_date
                            ? formatIndiaDateTime(row.effective_date)
                            : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm">{row.withdrawal_id}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm">
                          {row.player_name || `Player #${row.player_id}`}
                        </td>
                        <td className="px-4 py-3 text-sm">{row.player_mobile}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold">
                          {formatCurrency(Number(row.amount))}
                        </td>
                        <td className="px-4 py-3 text-sm">{row.status}</td>
                        <td className="px-4 py-3 text-sm">{row.gateway || '-'}</td>
                        <td className="px-4 py-3 text-sm">
                          {row.gateway_transaction_id || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {row.bank_transaction_id || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-5 py-4">
              <p className="text-sm text-gray-600">
                Page {withdrawalReportPage} of{' '}
                {Math.max(
                  1,
                  Math.ceil(
                    withdrawalReportTotalCount / withdrawalReportPageSize,
                  ),
                )}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={withdrawalReportLoading || withdrawalReportPage <= 1}
                  onClick={() =>
                    loadWithdrawalReport(
                      withdrawalReportPage - 1,
                      withdrawalReportAppliedFrom,
                      withdrawalReportAppliedTo,
                    )
                  }
                  className="rounded-lg border px-4 py-2 text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={
                    withdrawalReportLoading ||
                    withdrawalReportPage * withdrawalReportPageSize >=
                      withdrawalReportTotalCount
                  }
                  onClick={() =>
                    loadWithdrawalReport(
                      withdrawalReportPage + 1,
                      withdrawalReportAppliedFrom,
                      withdrawalReportAppliedTo,
                    )
                  }
                  className="rounded-lg border px-4 py-2 text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {manualDepositModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">

            <div className="flex flex-col gap-4 border-b border-gray-200 px-5 py-4 sm:px-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Manual Deposits - Detailed Report
                  </h2>

                  <p className="mt-1 text-sm text-gray-500">
                    View manual wallet deposits with player,
                    balance and administrator details.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={closeManualDeposits}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3 lg:grid-cols-6">
                <div className="lg:col-span-2">
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Date From
                  </label>

                  <input
                    type="date"
                    value={manualDepositDateFrom}
                    onChange={(e) =>
                      setManualDepositDateFrom(e.target.value)
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>

                <div className="lg:col-span-2">
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Date To
                  </label>

                  <input
                    type="date"
                    value={manualDepositDateTo}
                    onChange={(e) =>
                      setManualDepositDateTo(e.target.value)
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>

                <div className="flex items-end gap-2 lg:col-span-2">
                  <button
                    type="button"
                    onClick={applyManualDepositFilters}
                    disabled={manualDepositLoading}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    Apply
                  </button>

                  <button
                    type="button"
                    onClick={resetManualDepositFilters}
                    disabled={manualDepositLoading}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Today
                  </button>
                </div>
              </div>

              {manualDepositError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {manualDepositError}
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-amber-100 bg-amber-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                    Total Manual Deposits
                  </p>

                  <p className="mt-1 text-2xl font-bold text-amber-900">
                    {formatCurrency(manualDepositTotalAmount)}
                  </p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Transactions
                  </p>

                  <p className="mt-1 text-2xl font-bold text-gray-900">
                    {formatNumber(manualDepositTotalCount)}
                  </p>
                </div>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-auto">
              {manualDepositLoading ? (
                <div className="p-12 text-center text-sm text-gray-500">
                  Loading manual deposits...
                </div>
              ) : manualDepositRows.length === 0 ? (
                <div className="p-12 text-center">
                  <p className="text-sm font-medium text-gray-700">
                    No manual deposits found.
                  </p>

                  <p className="mt-1 text-xs text-gray-500">
                    Try a different date range.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="sticky top-0 z-10 bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Date / Time
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Player
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Mobile
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Amount
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Before
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                          After
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Added By
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Role
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Remark
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Source
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-100 bg-white">
                      {manualDepositRows.map((row) => {
                        const actorName =
                          row.added_by?.name ||
                          row.added_by?.mobile_number ||
                          'Unknown';

                        const rowKey =
                          row.transaction_id ??
                          `${row.source}-${row.reference_id}`;

                        return (
                          <tr
                            key={rowKey}
                            className="hover:bg-gray-50"
                          >
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                              {row.created_at
                                ? formatIndiaDateTime(row.created_at)
                                : '-'}
                            </td>

                            <td className="whitespace-nowrap px-4 py-3">
                              <p className="text-sm font-semibold text-gray-900">
                                {row.player_name ||
                                  `Player #${row.player_id}`}
                              </p>

                              <p className="mt-0.5 text-xs text-gray-400">
                                {row.journal_id
                                  ? `Journal #${row.journal_id}`
                                  : `Reference #${row.reference_id}`}
                              </p>
                            </td>

                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                              {row.player_mobile}
                            </td>

                            <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-bold text-emerald-700">
                              +{formatCurrency(Number(row.amount || 0))}
                            </td>

                            <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700">
                              {row.balance_before !== null &&
                              row.balance_before !== undefined
                                ? formatCurrency(
                                    Number(row.balance_before || 0),
                                  )
                                : '-'}
                            </td>

                            <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700">
                              {row.balance_after !== null &&
                              row.balance_after !== undefined
                                ? formatCurrency(
                                    Number(row.balance_after || 0),
                                  )
                                : '-'}
                            </td>

                            <td className="whitespace-nowrap px-4 py-3">
                              <p className="text-sm font-medium text-gray-900">
                                {actorName}
                              </p>

                              {row.added_by?.name &&
                                row.added_by?.mobile_number && (
                                  <p className="mt-0.5 text-xs text-gray-500">
                                    {row.added_by.mobile_number}
                                  </p>
                                )}
                            </td>

                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                              {row.added_by?.role || '-'}
                            </td>

                            <td className="min-w-[220px] px-4 py-3 text-sm text-gray-600">
                              {row.remark || '-'}
                            </td>

                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                              {row.source_label ||
                                row.source ||
                                '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3 border-t border-gray-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <p className="text-sm text-gray-500">
                Page {manualDepositPage} of{' '}
                {Math.max(
                  1,
                  Math.ceil(
                    manualDepositTotalCount /
                      manualDepositPageSize,
                  ),
                )}
              </p>

              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={
                    manualDepositLoading ||
                    manualDepositPage <= 1
                  }
                  onClick={() =>
                    loadManualDeposits(
                      manualDepositPage - 1,
                    )
                  }
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>

                <button
                  type="button"
                  disabled={
                    manualDepositLoading ||
                    manualDepositPage *
                      manualDepositPageSize >=
                      manualDepositTotalCount
                  }
                  onClick={() =>
                    loadManualDeposits(
                      manualDepositPage + 1,
                    )
                  }
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {manualDeductionModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">

            <div className="flex flex-col gap-4 border-b border-gray-200 px-5 py-4 sm:px-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Manual Deductions - Detailed Report
                  </h2>

                  <p className="mt-1 text-sm text-gray-500">
                    View manual wallet deductions with player,
                    balance and administrator details.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={closeManualDeductions}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3 lg:grid-cols-6">
                <div className="lg:col-span-2">
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Date From
                  </label>

                  <input
                    type="date"
                    value={manualDeductionDateFrom}
                    onChange={(e) =>
                      setManualDeductionDateFrom(e.target.value)
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>

                <div className="lg:col-span-2">
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Date To
                  </label>

                  <input
                    type="date"
                    value={manualDeductionDateTo}
                    onChange={(e) =>
                      setManualDeductionDateTo(e.target.value)
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>

                <div className="flex items-end gap-2 lg:col-span-2">
                  <button
                    type="button"
                    onClick={applyManualDeductionFilters}
                    disabled={manualDeductionLoading}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    Apply
                  </button>

                  <button
                    type="button"
                    onClick={resetManualDeductionFilters}
                    disabled={manualDeductionLoading}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Today
                  </button>
                </div>
              </div>

              {manualDeductionError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {manualDeductionError}
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-rose-100 bg-rose-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-rose-600">
                    Total Manual Deductions
                  </p>

                  <p className="mt-1 text-2xl font-bold text-rose-900">
                    {formatCurrency(manualDeductionTotalAmount)}
                  </p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Transactions
                  </p>

                  <p className="mt-1 text-2xl font-bold text-gray-900">
                    {formatNumber(manualDeductionTotalCount)}
                  </p>
                </div>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-auto">
              {manualDeductionLoading ? (
                <div className="p-12 text-center text-sm text-gray-500">
                  Loading manual deductions...
                </div>
              ) : manualDeductionRows.length === 0 ? (
                <div className="p-12 text-center">
                  <p className="text-sm font-medium text-gray-700">
                    No manual deductions found.
                  </p>

                  <p className="mt-1 text-xs text-gray-500">
                    Try a different date range.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="sticky top-0 z-10 bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Date / Time
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Player
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Mobile
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Amount
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Before
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                          After
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Deducted By
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Role
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Remark
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-100 bg-white">
                      {manualDeductionRows.map((row) => {
                        const actorName =
                          row.deducted_by?.name ||
                          row.deducted_by?.mobile_number ||
                          'Unknown';

                        return (
                          <tr
                            key={row.transaction_id}
                            className="hover:bg-gray-50"
                          >
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                              {row.created_at
                                ? formatDate(row.created_at)
                                : '-'}
                            </td>

                            <td className="whitespace-nowrap px-4 py-3">
                              <p className="text-sm font-semibold text-gray-900">
                                {row.player_name ||
                                  `Player #${row.player_id}`}
                              </p>

                              <p className="mt-0.5 text-xs text-gray-400">
                                Journal #{row.journal_id}
                              </p>
                            </td>

                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                              {row.player_mobile}
                            </td>

                            <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-bold text-rose-700">
                              -{formatCurrency(Number(row.amount || 0))}
                            </td>

                            <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700">
                              {formatCurrency(
                                Number(row.balance_before || 0),
                              )}
                            </td>

                            <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700">
                              {formatCurrency(
                                Number(row.balance_after || 0),
                              )}
                            </td>

                            <td className="whitespace-nowrap px-4 py-3">
                              <p className="text-sm font-medium text-gray-900">
                                {actorName}
                              </p>

                              {row.deducted_by?.name &&
                                row.deducted_by?.mobile_number && (
                                  <p className="mt-0.5 text-xs text-gray-500">
                                    {row.deducted_by.mobile_number}
                                  </p>
                                )}
                            </td>

                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                              {row.deducted_by?.role || '-'}
                            </td>

                            <td className="min-w-[220px] px-4 py-3 text-sm text-gray-600">
                              {row.remark || '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3 border-t border-gray-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <p className="text-sm text-gray-500">
                Page {manualDeductionPage} of{' '}
                {Math.max(
                  1,
                  Math.ceil(
                    manualDeductionTotalCount /
                      manualDeductionPageSize,
                  ),
                )}
              </p>

              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={
                    manualDeductionLoading ||
                    manualDeductionPage <= 1
                  }
                  onClick={() =>
                    loadManualDeductions(
                      manualDeductionPage - 1,
                    )
                  }
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>

                <button
                  type="button"
                  disabled={
                    manualDeductionLoading ||
                    manualDeductionPage *
                      manualDeductionPageSize >=
                      manualDeductionTotalCount
                  }
                  onClick={() =>
                    loadManualDeductions(
                      manualDeductionPage + 1,
                    )
                  }
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {registeredUsersModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4 sm:px-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Registered Users - {selectedPeriodLabel}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  {formatNumber(registeredUsersTotal)} user
                  {registeredUsersTotal === 1 ? '' : 's'} found
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={exportRegisteredUsersToExcel}
                  disabled={
                    registeredUsersExporting ||
                    registeredUsersLoading ||
                    registeredUsersTotal === 0
                  }
                  className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {registeredUsersExporting
                    ? 'Exporting...'
                    : 'Export Excel'}
                </button>

                <button
                  type="button"
                  onClick={closeRegisteredUsers}
                  className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-auto">
              {registeredUsersError ? (
                <div className="m-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                  {registeredUsersError}
                </div>
              ) : registeredUsersLoading ? (
                <div className="p-10 text-center text-sm text-gray-500">
                  Loading registered users...
                </div>
              ) : registeredUsers.length === 0 ? (
                <div className="p-10 text-center text-sm text-gray-500">
                  No registered users found for this period.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="sticky top-0 bg-gray-50">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          User
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Mobile
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Status
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Joined
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          App
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-100 bg-white">
                      {registeredUsers.map((user) => {
                        const userName =
                          user.full_name?.trim() ||
                          `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
                          `User #${user.id}`;

                        return (
                          <tr key={user.id} className="hover:bg-gray-50">
                            <td className="whitespace-nowrap px-5 py-4">
                              <p className="text-sm font-semibold text-gray-900">
                                {userName}
                              </p>
                              <p className="mt-1 text-xs text-gray-500">
                                ID: {user.id}
                              </p>
                            </td>

                            <td className="whitespace-nowrap px-5 py-4 text-sm text-gray-700">
                              {user.mobile_number || '-'}
                            </td>

                            <td className="whitespace-nowrap px-5 py-4">
                              <span
                                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                  user.is_active
                                    ? 'bg-green-50 text-green-700'
                                    : 'bg-red-50 text-red-700'
                                }`}
                              >
                                {user.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>

                            <td className="whitespace-nowrap px-5 py-4 text-sm text-gray-600">
                              {user.date_joined
                                ? formatIndiaDateTime(user.date_joined)
                                : '-'}
                            </td>

                            <td className="whitespace-nowrap px-5 py-4 text-sm text-gray-600">
                              {user.app_name || '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3 border-t border-gray-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <p className="text-sm text-gray-500">
                Page {registeredUsersPage} of{' '}
                {Math.max(
                  1,
                  Math.ceil(
                    registeredUsersTotal / registeredUsersPageSize,
                  ),
                )}
              </p>

              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={
                    registeredUsersLoading ||
                    registeredUsersPage <= 1
                  }
                  onClick={() =>
                    loadRegisteredUsers(registeredUsersPage - 1)
                  }
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>

                <button
                  type="button"
                  disabled={
                    registeredUsersLoading ||
                    registeredUsersPage *
                      registeredUsersPageSize >=
                      registeredUsersTotal
                  }
                  onClick={() =>
                    loadRegisteredUsers(registeredUsersPage + 1)
                  }
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {zeroBalanceModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4 sm:px-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {zeroBalanceMode === 'active'
                    ? `Active Zero Balance - ${selectedPeriodLabel}`
                    : 'Zero Balance Players'}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  {formatNumber(zeroBalanceTotal)} player
                  {zeroBalanceTotal === 1 ? '' : 's'} found
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={exportZeroBalancePlayersToExcel}
                  disabled={
                    zeroBalanceExporting ||
                    zeroBalancePlayersLoading ||
                    zeroBalanceTotal === 0
                  }
                  className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {zeroBalanceExporting
                    ? 'Exporting...'
                    : 'Export Excel'}
                </button>

                <button
                  type="button"
                  onClick={closeZeroBalancePlayers}
                  className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-auto">
              {zeroBalancePlayersError ? (
                <div className="m-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                  {zeroBalancePlayersError}
                </div>
              ) : zeroBalancePlayersLoading ? (
                <div className="p-10 text-center text-sm text-gray-500">
                  Loading players...
                </div>
              ) : zeroBalancePlayers.length === 0 ? (
                <div className="p-10 text-center text-sm text-gray-500">
                  No players found for this filter.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="sticky top-0 bg-gray-50">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Player
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Mobile
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Wallet
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Status
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Last Login
                        </th>
                        <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Follow Up
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-100 bg-white">
                      {zeroBalancePlayers.map((player) => {
                        const playerName =
                          player.full_name?.trim() ||
                          `${player.first_name || ''} ${player.last_name || ''}`.trim() ||
                          `Player #${player.id}`;

                        return (
                          <tr key={player.id} className="hover:bg-gray-50">
                            <td className="whitespace-nowrap px-5 py-4">
                              <p className="text-sm font-semibold text-gray-900">
                                {playerName}
                              </p>
                              <p className="mt-1 text-xs text-gray-500">
                                ID: {player.id}
                              </p>
                            </td>

                            <td className="whitespace-nowrap px-5 py-4 text-sm text-gray-700">
                              {player.mobile_number}
                            </td>

                            <td className="whitespace-nowrap px-5 py-4 text-sm font-semibold text-gray-900">
                              {player.points_formatted ||
                                formatCurrency(Number(player.points || 0))}
                            </td>

                            <td className="whitespace-nowrap px-5 py-4">
                              <span
                                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                  player.is_active
                                    ? 'bg-green-50 text-green-700'
                                    : 'bg-red-50 text-red-700'
                                }`}
                              >
                                {player.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>

                            <td className="whitespace-nowrap px-5 py-4 text-sm text-gray-600">
                              {player.last_login
                                ? formatDate(player.last_login)
                                : 'Never'}
                            </td>

                            <td className="whitespace-nowrap px-5 py-4 text-right">
                              <a
                                href={`tel:${player.mobile_number}`}
                                className="inline-flex rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700"
                              >
                                Call
                              </a>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3 border-t border-gray-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <p className="text-sm text-gray-500">
                Page {zeroBalancePage} of{' '}
                {Math.max(
                  1,
                  Math.ceil(zeroBalanceTotal / zeroBalancePageSize),
                )}
              </p>

              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={
                    zeroBalancePlayersLoading ||
                    zeroBalancePage <= 1
                  }
                  onClick={() =>
                    loadZeroBalancePlayers(
                      zeroBalanceMode,
                      zeroBalancePage - 1,
                    )
                  }
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>

                <button
                  type="button"
                  disabled={
                    zeroBalancePlayersLoading ||
                    zeroBalancePage *
                      zeroBalancePageSize >=
                      zeroBalanceTotal
                  }
                  onClick={() =>
                    loadZeroBalancePlayers(
                      zeroBalanceMode,
                      zeroBalancePage + 1,
                    )
                  }
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
